/**
 * The suspected-reaction path (SUP-003, SUP-004). Emergency instructions come first. A signed-in
 * customer can file a report against their formula. Anyone can still email support.
 */
import * as stylex from "@stylexjs/stylex";
import type { FormEvent } from "react";
import { useEffect, useRef, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Button } from "@lattice/ui/Button";
import { focusFirstError } from "@lattice/ui/focusFirstError";
import { Field } from "@lattice/ui/Field";
import { CheckIcon, WarningIcon } from "@lattice/ui/icons";
import { RadioGroup } from "@lattice/ui/RadioGroup";
import { Textarea } from "@lattice/ui/Textarea";
import { Input } from "@lattice/ui/Input";
import { Link } from "@lattice/ui/Link";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Pending } from "@lattice/ui/Pending";
import { Status } from "@lattice/ui/Status";
import { loadMe, reportReaction } from "./api.ts";
import { PublicPage, SUPPORT_ADDRESS, supportMailto } from "./PublicPage.tsx";
const SEVERITIES = [{
  value: "mild",
  label: "Mild",
  description: "Noticeable, but you can go about your day."
}, {
  value: "moderate",
  label: "Moderate",
  description: "It gets in the way of your day."
}, {
  value: "severe",
  label: "Severe",
  description: "Hard to cope with, or it is getting worse."
}] as const;
type Severity = (typeof SEVERITIES)[number]["value"];
function describeProblem(description: string): string | null {
  return description.trim() === "" ? "Write what happened, even in a few words." : null;
}
function emailProblem(email: string): string | null {
  if (email.trim() === "") return "Enter your email so we can write back.";
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? null : "That email looks incomplete. Check the part after the @, like name@example.com.";
}
function Safety() {
  return <div className="x78zum5 x1cy8zhl x1173whf x1wg0vgl x1fbwzx x1n3p3y5 x1j243yh">
      <WarningIcon />
      <div>
        <p className="x1ghz6dp xm8xz4y xqu410z">
          <strong>
            Call <Link href="tel:911">911</Link>
          </strong>{" "}
          if this is an emergency.
        </p>
        <p className="x1ghz6dp xm8xz4y xqu410z">
          Stop taking the formula until a physician writes back.
        </p>
        <p className="x1ghz6dp xm8xz4y xqu410z">
          For poisoning advice at any hour, call Poison Help on{" "}
          <Link href="tel:+18002221222" className="xuxw1ft">
            1-800-222-1222
          </Link>
          .
        </p>
      </div>
    </div>;
}
function Notes({
  severity,
  failed,
  accountId,
  draft
}: {
  readonly severity: Severity | null;
  readonly failed: "failed" | "limited" | null;
  readonly accountId: string | null;
  readonly draft: string;
}) {
  return <>
      {severity === "severe" ? <Status state="problem">
          Trouble breathing, swelling of the face or throat, or chest pain: call 911 now. Then send
          this report.
        </Status> : null}
      {failed === null ? null : <div role="alert" className="x78zum5 x1cy8zhl x1173whf x6ygkrl">
          <WarningIcon />
          <p className="x1ghz6dp xl4dl4e x6ygkrl">
            Your report was not sent.{" "}
            {failed === "limited" ? "You have sent several reports in the last hour, so try again later or write to " : "Try again, or write to "}
            <Link href={supportMailto(accountId, draft)}>{SUPPORT_ADDRESS}</Link> now. What you
            wrote is already in the email.
          </p>
        </div>}
    </>;
}

/** Focus goes here after sending, because the form that held it is gone. */
function Sent({
  accountId,
  email,
  onAnother
}: {
  readonly accountId: string | null;
  readonly email: string | null;
  readonly onAnother: () => void;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus();
  }, []);
  return <div className="x78zum5 xdt5ytf x15agyyk xh8yej3">
      <h2 ref={heading} tabIndex={-1} className="x1ghz6dp x11mdwsw xqu410z x1e5flnj">
        Report sent
      </h2>
      <div role="status" data-testid="reaction-sent" className="x78zum5 x1cy8zhl x626il7 x1wg0vgl x1fbwzx xl5rrbb xdhxsyf">
        <CheckIcon />
        <p className="x1ghz6dp xm8xz4y">
          A physician has this report and will write to you{email === null ? "" : ` at ${email}`}.
          Do not take the formula until they write back. Call 911 if you feel worse.
        </p>
      </div>
      <Button variant="secondary" onClick={onAnother}>
        Send another report
      </Button>
      <LinkButton href="/account" tone="quiet">
        Back to your account
      </LinkButton>
      <p className="x1ghz6dp x1jty25b x1fzhlzt">
        Prefer email? Write to <Link href={supportMailto(accountId)}>{SUPPORT_ADDRESS}</Link>.
      </p>
    </div>;
}
function ReactionForm({
  accountId,
  onSent
}: {
  readonly accountId: string | null;
  readonly onSent: (email: string | null) => void;
}) {
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [severity, setSeverity] = useState<Severity | null>(null);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState<"failed" | "limited" | null>(null);
  const [tried, setTried] = useState(false);

  /* A report is words typed while unwell, so leaving the page asks first. */
  useEffect(() => {
    if (description.trim() === "") return undefined;
    const ask = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", ask);
    return () => window.removeEventListener("beforeunload", ask);
  }, [description]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTried(true);
    if (describeProblem(description) !== null || accountId === null && emailProblem(email) !== null) {
      focusFirstError(event.currentTarget);
      return;
    }
    if (sending) return;
    setSending(true);
    setFailed(null);
    const result = await reportReaction(description.trim(), severity, accountId === null ? email.trim() : null);
    setSending(false);
    if (result === "sent") onSent(accountId === null ? email.trim() : null);else setFailed(result);
  }
  const problems = tried ? [{
    id: "reaction-description",
    message: describeProblem(description)
  }, ...(accountId === null ? [{
    id: "reaction-email",
    message: emailProblem(email)
  }] : [])].filter(problem => problem.message !== null) : [];
  return <form noValidate onSubmit={event => void submit(event)} className="x78zum5 xdt5ytf x15agyyk xh8yej3">
      {problems.length > 1 ? <div role="alert" className="x78zum5 x1cy8zhl x1173whf x6ygkrl">
          <WarningIcon />
          <div>
            <p className="x1ghz6dp xl4dl4e x6ygkrl">{problems.length} things to fix</p>
            <ul className="x1ghz6dp x1ua174w xl4dl4e">
              {problems.map(problem => <li key={problem.id}>
                  <a href={`#${problem.id}`}>{problem.message}</a>
                </li>)}
            </ul>
          </div>
        </div> : null}
      <Field label="What happened" hint="What you took, when, and what you felt. A few words is enough." id="reaction-description" {...description.length >= 1800 ? {
      counter: {
        length: description.length,
        max: 2000
      }
    } : {}} error={tried ? describeProblem(description) : null}>
        {control => <Textarea {...control} name="description" rows={5} autoGrow maxRows={14} maxLength={2000} value={description} onChange={value => {
        setDescription(value);

        /* New words are a new attempt, so a refusal for sending too many no longer holds the button. */
        if (failed === "limited") setFailed(null);
      }} />}
      </Field>
      {accountId === null ? <Field label="Your email" hint="Used only to write back about this report." id="reaction-email" error={tried ? emailProblem(email) : null}>
          {control => <Input {...control} type="email" name="email" autoComplete="email" inputMode="email" value={email} onChange={setEmail} />}
        </Field> : null}
      <RadioGroup label="How it felt, if you can say" optional name="severity" options={SEVERITIES} variant="cards" value={severity} onChange={setSeverity} />
      <Notes severity={severity} failed={failed} accountId={accountId} draft={description.trim()} />
      <Button type="submit" fullWidth loading={sending} disabled={failed === "limited"} disabledReason={failed === "limited" ? "You have sent several reports in the last hour" : undefined} testId="send-reaction">
        Send this report
      </Button>
    </form>;
}
export function Reaction() {
  const [accountId, setAccountId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [sent, setSent] = useState<{
    readonly email: string | null;
  } | null>(null);
  useEffect(() => {
    /* A failed lookup is treated as signed out, so the person still gets the way to write to us. */
    void loadMe().catch(() => null).then(me => {
      setAccountId(me?.userId ?? null);
      setReady(true);
    });
  }, []);
  return <PublicPage title="Report a reaction · Lattice">
      <div className="xrvj5dj x1mkdm3x x14ixu2r xtd2lgx x1ewte8j x7a106z">
        <div className="x78zum5 xdt5ytf x626il7 x1uhb9sk xqr1kp1 x3vkroj">
          <h1 className="x1ghz6dp x1u29x3t xqu410z x1ntj4s9 x1d3j7mx x1e5flnj x1w2vvpw">Report a reaction</h1>
          <Safety />
          {sent === null ? <p className="x1ghz6dp x1jty25b x1fzhlzt">
              A physician reads every report. Write what happened.
            </p> : null}
        </div>
        {sent !== null ? <Sent accountId={accountId} email={sent.email} onAnother={() => setSent(null)} /> : !ready ? <Pending message="Checking your sign-in" bones={["control"]} /> : <div className="x78zum5 xdt5ytf x15agyyk xh8yej3">
            <ReactionForm accountId={accountId} onSent={email => setSent({
          email
        })} />
            <p className="x1ghz6dp x1jty25b x1fzhlzt">
              Prefer email? Write to <Link href={supportMailto(accountId)}>{SUPPORT_ADDRESS}</Link>.
            </p>
          </div>}
      </div>
    </PublicPage>;
}